import { Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { NotifyMeForm } from "./NotifyMeForm";
import { useCurrentUser } from "@/hooks/use-current-user";

interface ZeroResultsStateProps {
  filters: Record<string, unknown>;
  hasActiveFilters: boolean;
  onClearFilters?: () => void;
  accessToken?: string;
  /** Offering type this zero-result Notify-Me is scoped to (ADR-088). */
  offeringType?: string;
}

export function ZeroResultsState({
  filters,
  hasActiveFilters,
  onClearFilters,
  accessToken,
  offeringType,
}: ZeroResultsStateProps) {
  const isAuthenticated = !!accessToken;
  const { user: currentUser } = useCurrentUser();
  const accountEmail = currentUser?.email ?? null;
  return (
    <div className="text-center py-24">
      {/* Icon */}
      <div className="w-16 h-16 bg-[#EEEFF9] rounded-2xl flex items-center justify-center mx-auto mb-5">
        <Search className="w-7 h-7 text-[#A9ACD6]" aria-hidden="true" />
      </div>

      {/* Heading */}
      <h3 className="text-xl font-bold text-[#262A7A] mb-2">No offerings found</h3>
      <p className="text-sm text-[#5A5E80] mb-6 max-w-sm mx-auto">
        Try adjusting your filters or broadening your search.
      </p>

      {/* Suggestions */}
      <ul className="text-sm text-[#5A5E80] mb-6 max-w-xs mx-auto text-left list-disc list-inside space-y-1">
        <li>Remove some filters</li>
        <li>Try different keywords</li>
        <li>Check back later for new offerings</li>
      </ul>

      {/* Clear filters */}
      {hasActiveFilters && onClearFilters && (
        <Button
          onClick={onClearFilters}
          variant="outline"
          className="rounded-full px-6 cursor-pointer mb-8 transition-colors duration-150 hover:bg-accent"
        >
          Clear all filters
        </Button>
      )}

      {/* Notify Me section — shown only when filters are active */}
      {hasActiveFilters && (
        <>
          <div className="flex items-center gap-3 max-w-sm mx-auto mb-6">
            <div className="flex-1 h-px bg-border" />
            <span className="text-xs text-[#A9ACD6] uppercase tracking-wide">or</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          <p className="text-sm font-bold text-[#262A7A] mb-3">
            Get notified when matching offerings appear
          </p>

          <div className="flex justify-center">
            <NotifyMeForm
              filterState={filters}
              offeringType={offeringType}
              isAuthenticated={isAuthenticated}
              accountEmail={accountEmail}
              accessToken={accessToken}
            />
          </div>
        </>
      )}
    </div>
  );
}
